/**
 * Humanized relative-time formatting (DNKADM-0047 U2).
 *
 * Renders an ISO instant as a short "… ago" / "in …" phrase for the
 * notifications meta line ("last 10 min ago"), using the native
 * `Intl.RelativeTimeFormat` — NO new dependency, no date library. Degrades, never
 * throws: a null or unparseable instant returns `null` so the caller can omit the
 * phrase rather than render a placeholder mid-sentence.
 */

/** Threshold ladder (seconds → unit) from finest to coarsest, largest divisor last. */
const DIVISIONS: ReadonlyArray<{ limit: number; unit: Intl.RelativeTimeFormatUnit; per: number }> =
  [
    { limit: 60, unit: 'second', per: 1 },
    { limit: 3600, unit: 'minute', per: 60 },
    { limit: 86_400, unit: 'hour', per: 3600 },
    { limit: 604_800, unit: 'day', per: 86_400 },
    { limit: 2_629_800, unit: 'week', per: 604_800 },
    { limit: 31_557_600, unit: 'month', per: 2_629_800 },
    { limit: Infinity, unit: 'year', per: 31_557_600 },
  ];

const RTF = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto', style: 'short' });

/**
 * Format an ISO instant relative to `now` (defaults to the current time), e.g.
 * `"10 min ago"`, `"2 days ago"`, `"just now"`. Returns `null` for a null or
 * unparseable input. Future instants render as "in …" (tolerated; the API's
 * `lastSeenAt` is in the past, but we never assume clock alignment).
 */
export function formatRelativeTime(iso: string | null, now: number = Date.now()): string | null {
  if (iso === null) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;

  const deltaSeconds = Math.round((ms - now) / 1000);
  const abs = Math.abs(deltaSeconds);
  if (abs < 45) return 'just now';

  for (const { limit, unit, per } of DIVISIONS) {
    if (abs < limit) {
      return RTF.format(Math.round(deltaSeconds / per), unit);
    }
  }
  // Unreachable (the last division's limit is Infinity), but keep the contract total.
  return null;
}
